// src/components/ErrorBoundary.jsx - ROBUST VERSION
import React from 'react';
import { logger } from "../utils/logger";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: Date.now() // Unique ID for this error instance
    };
  }

  componentDidCatch(error, errorInfo) {
  // Create a unique error ID for tracking
  const errorId = this.state.errorId || Date.now();
  
  // Cache error for potential debugging
  if (!window.__errorCache) window.__errorCache = {};
  window.__errorCache[errorId] = {
    error: error.message,
    stack: error.stack,
    componentStack: errorInfo.componentStack,
    timestamp: new Date().toISOString(),
    url: window.location.href
  };
  
  // Enhanced error logging
  console.group('🚨 ErrorBoundary caught an error');
  console.error('Error:', error);
  console.error('Error Info:', errorInfo);
  console.error('Error ID:', errorId);
  console.error('Timestamp:', new Date().toISOString());
  console.error('User Agent:', navigator?.userAgent);
  console.error('URL:', window?.location?.href);
  console.groupEnd();

  // Safely update state
  this.setState({
    error: error || new Error('Unknown error occurred'),
    errorInfo: errorInfo || { componentStack: 'Component stack not available' }
  });

  // Optional: Report to error tracking service
  if (this.props.onError && typeof this.props.onError === 'function') {
    try {
      this.props.onError(error, errorInfo, errorId);
    } catch (reportingError) {
      console.error('❌ Error reporting failed:', reportingError);
    }
  }
}

  handleRetry = () => {
  logger.component('🔄 ErrorBoundary: User initiated retry');
  
  // Reset error state
  this.setState({ 
    hasError: false, 
    error: null, 
    errorInfo: null,
    errorId: null
  });
  
  // Clear any error-related cache
  if (window.__errorCache && this.state.errorId) {
    delete window.__errorCache[this.state.errorId];
  }
  
  // Reset error count for this session
  window.__errorCount = 0;
  
  // Optional: Trigger a callback to let parent components know about retry
  if (typeof this.props.onRetry === 'function') {
    try {
      this.props.onRetry();
    } catch (retryError) {
      console.error('❌ Error in retry callback:', retryError);
    }
  }
};

  handleRefresh = () => {
    window.location.reload();
  };

  componentDidUpdate(prevProps, prevState) {
  // Handle recovery from error state
  if (prevState.hasError && !this.state.hasError) {
    logger.component('🔄 ErrorBoundary: Recovered from error state');
    
    // Clear any stale data when recovering
    if (typeof this.props.onRecover === 'function') {
      try {
        this.props.onRecover();
      } catch (recoveryError) {
        console.error('❌ Error during recovery callback:', recoveryError);
      }
    }
    
    // Clear any cached error data
    if (window.__errorCache) {
      delete window.__errorCache[this.state.errorId];
    }
  }
  
  // If we keep getting the same error repeatedly, take special action
  if (this.state.hasError && prevState.errorId !== this.state.errorId) {
    const errorCount = (window.__errorCount || 0) + 1;
    window.__errorCount = errorCount;
    
    if (errorCount >= 3) {
      console.error('🚨 Multiple errors detected, suggesting page refresh');
      // Could show a different message for repeated errors
    }
  }
}

  render() {
    if (this.state.hasError) {
      // Safely get error details
      const errorMessage = this.state.error?.message || this.state.error?.toString() || 'An unknown error occurred';
      const errorStack = this.state.error?.stack || 'Stack trace not available';
      const componentStack = this.state.errorInfo?.componentStack || 'Component stack not available';
      
      return (
        <div style={{
          padding: '2rem',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '20px auto',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          
          <h2 style={{ 
            margin: '0 0 1rem 0',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            Oops! Something went wrong
          </h2>
          
          <p style={{ 
            margin: '0 0 1.5rem 0',
            fontSize: '1rem',
            color: '#7f1d1d'
          }}>
            This part of the application encountered an error and couldn't be displayed.
          </p>

          {/* User-friendly error summary */}
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            padding: '1rem',
            marginBottom: '1.5rem',
            textAlign: 'left'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>
              Error Details:
            </h4>
            <p style={{ 
              margin: '0', 
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              wordBreak: 'break-word'
            }}>
              {errorMessage}
            </p>
          </div>
          
          {/* Development-only detailed error info */}
          {process.env.NODE_ENV === 'development' && (
            <details style={{ 
              textAlign: 'left', 
              backgroundColor: '#fecaca', 
              padding: '1rem', 
              borderRadius: '6px',
              marginBottom: '1.5rem'
            }}>
              <summary style={{ 
                cursor: 'pointer', 
                fontWeight: 'bold',
                marginBottom: '0.5rem'
              }}>
                🔍 Technical Details (Development Mode)
              </summary>
              
              <div style={{ marginTop: '1rem' }}>
                <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem' }}>Error Stack:</h5>
                <pre style={{ 
                  whiteSpace: 'pre-wrap', 
                  fontSize: '0.75rem',
                  margin: '0 0 1rem 0',
                  padding: '0.5rem',
                  backgroundColor: '#fed7d7',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '150px'
                }}>
                  {errorStack}
                </pre>
                
                <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem' }}>Component Stack:</h5>
                <pre style={{ 
                  whiteSpace: 'pre-wrap', 
                  fontSize: '0.75rem',
                  margin: '0',
                  padding: '0.5rem',
                  backgroundColor: '#fed7d7',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '150px'
                }}>
                  {componentStack}
                </pre>
              </div>
            </details>
          )}
          
          {/* Action buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button 
              onClick={this.handleRetry}
              style={{
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
            >
              🔄 Try Again
            </button>
            
            <button 
              onClick={this.handleRefresh}
              style={{
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
            >
              🔄 Refresh Page
            </button>

            {this.props.fallbackComponent && (
              <button 
                onClick={() => this.setState({ hasError: false })}
                style={{
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#047857'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#059669'}
              >
                📋 Show Fallback
              </button>
            )}
          </div>

          {/* Error ID for support */}
          {this.state.errorId && (
            <div style={{
              marginTop: '1.5rem',
              padding: '0.75rem',
              backgroundColor: '#f3f4f6',
              borderRadius: '6px',
              fontSize: '0.75rem',
              color: '#6b7280'
            }}>
              Error ID: {this.state.errorId} • {new Date().toLocaleString()}
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.defaultProps = {
  onError: null,
  fallbackComponent: null
};

export default ErrorBoundary;

// Export a HOC version for easy wrapping
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};